import React, { CSSProperties, ReactElement, Ref } from 'react';
import { defined } from '@unifocus/js-utils';
import { PaddingType, Panel } from '../panel/Panel';
import { isReactText, reactNodeIsOfType } from '../../../utils';
import { Property } from 'csstype';
import {extractDomProps} from '../../../utils/DomPropsExtractor';
import { isElementOfType } from 'react-dom/test-utils';

/**
 * {@docCategory FlexPanel}
 */
export interface FlexPanelProps {
   /**
    * The direction of the flex box layout.
    *
    * @defaultvalue column
    */
   flexDirection?: Property.FlexDirection;

   /**
    * The flex setting. This only applies when the panel is inside of another FlexPanel.
    *
    * @defaultvalue 1
    */
   flex?: number | string;

   /**
    * The flex-wrap setting.
    */
   flexWrap?: Property.FlexWrap;

   /**
    * The justify-content setting.
    */
   justifyContent?: Property.JustifyContent;

   /**
    * The align-items setting.
    */
   alignItems?: Property.AlignItems;

   /**
    * The align-content setting.
    */
   alignContent?: Property.AlignContent;

   /**
    * Gap between panels.
    *
    * @defaultvalue undefined
    */
   gap?: boolean | 'small' | 'medium' | 'large';

   /**
    * The gap between the columns.
    *
    * @defaultvalue medium
    */
   hgap?: boolean | 'small' | 'medium' | 'large'

   /**
    * The gap between the rows.
    *
    * @defaultvalue medium
    */
   vgap?: boolean | 'small' | 'medium' | 'large'

   /**
    * The width of the panel.
    *
    * @defaultvalue 100%
    */
   width?: string | number;

   /**
    * The height of the panel.
    *
    * @defaultvalue 100%
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue true
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue true
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * Called when the panel is clicked.
    */
   onClick?: () => void;
}

/**
 * {@docCategory FlexPanel}
 */
export interface FlexItemProps {
   /**
    * The flex setting.
    */
   flex?: number | string;

   /**
    * The align-self setting.
    */
   align?: Property.AlignSelf;

   /**
    * The order setting.
    */
   order?: number;

   /**
    * Sets a className on the grid item.
    */
   className?: string;

   /**
    * Sets styles on the grid item.
    */
   style?: CSSProperties;
}

export const FlexPanel: React.FC<React.PropsWithChildren<FlexPanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: React.PropsWithChildren<FlexPanelProps>, ref: Ref<HTMLDivElement>) => {
      const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : true;
      const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : true;

      const hgap = defined(props.hgap) ? props.hgap : props.gap;
      const vgap = defined(props.vgap) ? props.vgap : props.gap;

      const flexDirection = props.flexDirection || 'row';

      const style: CSSProperties = {
         display: 'flex',
         flexDirection: flexDirection,
         flexWrap: props.flexWrap,
         justifyContent: props.justifyContent,
         alignItems: props.alignItems,
         alignContent: props.alignContent,
         flex: props.flex,
         ...props.style
      };

      let classNames = 'uf-flex-panel';

      classNames += ` uf-flex-panel-flex-direction-${flexDirection}`;

      if (props.flexWrap === 'wrap' || props.flexWrap === 'wrap-reverse') {
         classNames += ` uf-flex-panel-${props.flexWrap}`;
      }

      if (hgap === true) {
         classNames += ` uf-flex-panel-hgap-medium`;
      }
      else if (hgap) {
         classNames += ` uf-flex-panel-hgap-${hgap}`;
      }

      if (vgap === true) {
         classNames += ` uf-flex-panel-vgap-medium`;
      }
      else if (vgap) {
         classNames += ` uf-flex-panel-vgap-${vgap}`;
      }

      if (props.className) {
         classNames += ' ' + props.className;
      }

      return (
         <Panel
            {...extractDomProps(props)}
            className={classNames}
            border={props.border}
            padding={props.padding}
            paddingLeft={props.paddingLeft}
            paddingRight={props.paddingRight}
            paddingTop={props.paddingTop}
            paddingBottom={props.paddingBottom}
            scroll={props.scroll}
            width={props.width}
            height={props.height}
            minHeight={props.minHeight}
            minWidth={props.minWidth}
            maxHeight={props.maxHeight}
            maxWidth={props.maxWidth}
            verticalFill={verticalFill}
            horizontalFill={horizontalFill}
            style={style}
            ref={ref}
            id={props.id}
         >
            {applyFlexItemPropsToChildren(props)}
         </Panel>
      );
   });

const isFlexItem = (item: React.ReactNode): boolean => {
   return reactNodeIsOfType(item, FlexItem);
};

const applyFlexItemPropsToChildren = (props: React.PropsWithChildren<FlexPanelProps>) => {

   const childrenArray: ReactElement[] = [];

   React.Children.forEach(props.children, (child: any) => {
      if (isElementOfType(child, React.Fragment)) {
         child = (child.props as any).children;
      }
      childrenArray.push(child);
   });

   return React.Children.map(childrenArray, (child, index) => {
      const reactElement = child as React.ReactElement;
      if (isFlexItem(reactElement)) {
         const childToRender = reactElement.props.children;
         const style: CSSProperties = Object.assign({ ...reactElement.props.style },
                                                    childToRender && childToRender.props ? childToRender.props.style : {});

         if (reactElement.props.align) {
            style.alignSelf = reactElement.props.align;
         }

         if (reactElement.props.flex) {
            style.flex = reactElement.props.flex;
         }

         if (reactElement.props.order) {
            style.order = reactElement.props.order;
         }

         let classNames = 'uf-flex-item';

         if (reactElement.props.className) {
            classNames += ' ' + reactElement.props.className;
         }

         if (isReactText(childToRender)) {
            return <div style={style} className={classNames}>{childToRender}</div>
         }

         return childToRender ? React.cloneElement(childToRender, { style: style, className: classNames }) : null;
      }

      return reactElement;
   });
};

export const FlexItem = (props: React.PropsWithChildren<FlexItemProps>) => {
   return (
      <div>{props.children}</div>
   );
};
